import {
  assignmentOf,
  enqueue,
  getLocalDb,
  liveQuery,
  OutboxDrainer,
  queuePenalty,
  queueShot,
  removeQueuedPenalty,
  RESUMED_AFTER_SIGN_IN,
  unsentCountsFor,
  type CachedShot,
  type PenaltyAddPayload,
  type PenaltyCode,
  type Submitter,
} from '@flm9/sync';
import { api, isOffline, toApiError, UnauthenticatedError } from './api';
import { recordLandedPenalties } from './landedPenalties';
import { track, traceparentOf } from '@flm9/telemetry-client';

/** `lastError` of an entry paused by an expired login (UX review REF-10). */
export const AUTH_PAUSE_ERROR = 'http_401';

/**
 * Every queued entry carries `roundAssignmentId` (the drainer blocks per
 * assignment); `sessions.callShooter` names it `assignmentId`.
 */
function callShooterInput(payload: unknown) {
  const { roundAssignmentId, laneId } = payload as {
    roundAssignmentId: string;
    laneId?: string | null;
  };
  return { assignmentId: roundAssignmentId, ...(laneId !== undefined ? { laneId } : {}) };
}

/**
 * Notes a paused attempt (offline / 401 / gateway) for `wakeRefereeSync`.
 * Also the flight recorder's hook: every queued upload (shots, confirm,
 * call, DQ, penalties — the path the keypads actually use, also long after
 * an offline phase) becomes a background intent with its outcome, and the
 * request below joins its trace via `currentUploadTraceparent`.
 */
let currentUploadTraceparent: string | undefined;
function trackPause(submit: Submitter): Submitter {
  return async (entry, signal) => {
    const payload = (entry.payload ?? {}) as {
      roundAssignmentId?: string;
      clientIdempotencyKey?: string;
    };
    const handle = track(
      `Upload ${entry.op}`,
      {
        entryId: entry.id,
        op: entry.op,
        roundAssignmentId: payload.roundAssignmentId ?? null,
        clientKey: payload.clientIdempotencyKey ?? null,
      },
      { background: true },
    );
    currentUploadTraceparent = traceparentOf(handle);
    try {
      const result = await submit(entry, signal);
      if (!result.ok && result.pause) passPaused = true;
      if (result.ok) handle.ok({ uploaded: true });
      else
        handle.fail(result.error ?? 'failed', {
          retry: result.retry,
          pause: result.pause ?? false,
        });
      return result;
    } finally {
      currentUploadTraceparent = undefined;
    }
  };
}

/** One uploader for the PWA lifetime, independent of the selected shooter. */
export const refereeDrainer = new OutboxDrainer({
  submit: trackPause(async (entry, signal) => {
    const procedures: Record<string, string> = {
      'shot.add': 'shots.add',
      'shot.update': 'shots.update',
      'shot.remove': 'shots.remove',
      'penalty.add': 'penalties.add',
      'penalty.remove': 'penalties.remove',
      // Offline flow (UX review REF-3): queued behind the shots, FIFO.
      'round.complete': 'shots.confirmRound',
      'round.timing': 'shots.setRoundTiming',
      'round.call': 'sessions.callShooter',
      // Offline DQ (manual + PAR auto-DQ): FIFO before a later confirm.
      'round.dq': 'shots.disqualifyRound',
    };
    const procedure = procedures[entry.op];
    if (!procedure) return { ok: false, retry: false, error: `unsupported_op:${entry.op}` };
    const body = entry.op === 'round.call' ? callShooterInput(entry.payload) : entry.payload;
    try {
      const res = await fetch(`/api/trpc/${procedure}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': entry.id,
          ...(currentUploadTraceparent ? { traceparent: currentUploadTraceparent } : {}),
        },
        body: JSON.stringify(body),
        signal: AbortSignal.any([signal, AbortSignal.timeout(15_000)]),
      });
      if (res.ok) {
        // Before the ack: keep a landed penalty change visible until the
        // roster the score screen is created from shows it.
        await recordLandedPenalties([entry]);
        return { ok: true };
      }
      // Expired sessions must preserve queued work without consuming retries.
      if (res.status === 401)
        return { ok: false, retry: true, pause: true, error: AUTH_PAUSE_ERROR };
      // Gateway answering for a down server: offline, same as below.
      if ([502, 503, 504].includes(res.status))
        return { ok: false, retry: true, pause: true, error: `http_${res.status}` };
      const retry = res.status >= 500 || res.status === 408 || res.status === 429;
      // A rejection keeps the server's German reason, so the score screen
      // can say which change failed and why instead of a bare status.
      return {
        ok: false,
        retry,
        error: retry ? `http_${res.status}` : (await toApiError(res, procedure)).message,
      };
    } catch (err) {
      // Network failure (or our 15 s timeout): pause instead of burning
      // attempts — an hour offline at the range must not turn queued shots
      // into "fehlgeschlagen" (REF-3). The periodic wake below resumes.
      return { ok: false, retry: true, pause: true, error: String(err) };
    }
  }),
});

// A wake while a drain runs is not forwarded to the drainer: re-requesting
// would keep a drain stalled on a dead network (15 s timeouts) alive forever.
// But it is remembered: the running pass only picks up entries queued before
// its last outbox read, so an entry queued while the pass winds down (no work
// left, lease release) would otherwise wait for the 15 s tick. One follow-up
// pass runs unless the pass ended paused (offline / 401 / gateway) — then the
// tick and the `online` event resume as before.
let draining = false;
let wokenWhileDraining = false;
let passPaused = false;
export function wakeRefereeSync(): void {
  if (!navigator.onLine) return;
  if (draining) {
    wokenWhileDraining = true;
    return;
  }
  draining = true;
  wokenWhileDraining = false;
  passPaused = false;
  void refereeDrainer
    .drain()
    .catch((err) => console.error('sync failed', err))
    .finally(() => {
      draining = false;
      if (wokenWhileDraining && !passPaused) wakeRefereeSync();
    });
}

export function startRefereeSync(): () => void {
  wakeRefereeSync();
  // The helper must not leave rejected background promises unobserved.
  const onOnline = () => wakeRefereeSync();
  const onVisible = () => {
    if (document.visibilityState === 'visible') wakeRefereeSync();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  // A paused queue also resumes on its own: venue Wi-Fi can stay "online"
  // while the server is unreachable, so no `online` event would come.
  const tick = window.setInterval(wakeRefereeSync, 15_000);
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(tick);
  };
}

/**
 * Queue a round-level step that couldn't reach the server (offline flow, UX
 * review REF-3). FIFO behind the shots already queued, so e.g. a confirm can
 * never overtake them and a queued call lands before the shooter's shots.
 */
export async function queueRoundOp(
  op: 'round.complete' | 'round.timing' | 'round.call' | 'round.dq',
  payload: { roundAssignmentId: string } & Record<string, unknown>,
): Promise<void> {
  await enqueue(op, payload);
  wakeRefereeSync();
}

/** Try to send the queue now, but stop waiting after `ms` (dead network: 15 s timeouts). */
export async function drainRefereeSyncBounded(ms = 4_000): Promise<void> {
  await Promise.race([
    refereeDrainer.drain().catch(() => undefined),
    new Promise((resolve) => window.setTimeout(resolve, ms)),
  ]);
}

/**
 * Live unsent (pending + inflight) and rejected (failed_terminal) counts of
 * the given shooters (`null` = device-wide) — e.g. all rounds of one session
 * before Badeschluss (native app: `unsentForAssignments`). `onCounts` runs
 * now and after every outbox change. Returns the unsubscribe.
 */
export function subscribeUnsentForAssignments(
  ids: ReadonlySet<string> | null,
  onCounts: (counts: { pending: number; failed: number }) => void,
): () => void {
  const sub = liveQuery(() =>
    getLocalDb().outbox.where('status').anyOf('pending', 'inflight', 'failed_terminal').toArray(),
  ).subscribe({
    next: (rows) => onCounts(unsentCountsFor(rows, ids)),
    error: (err) => console.error('unsent count failed', err),
  });
  return () => sub.unsubscribe();
}

/**
 * Shooters whose confirmation is still queued. Until it lands, the server
 * (and any roster fetched meanwhile) still shows them as open — "Aufrufen"
 * must not pick them again.
 */
export async function queuedConfirmations(): Promise<Set<string>> {
  const rows = await getLocalDb()
    .outbox.where('status')
    .anyOf('pending', 'inflight')
    .filter((e) => e.op === 'round.complete')
    .toArray();
  return new Set(rows.map(assignmentOf).filter((id): id is string => id != null));
}

/**
 * After a new sign-in (REF-10): forget the "paused by 401" marks so the score
 * screen drops its banner right away, then resume the queue. An entry that
 * still gets a 401 is marked again on its next attempt.
 */
export async function resumeAfterSignIn(): Promise<void> {
  await getLocalDb()
    .outbox.where('status')
    .equals('pending')
    .filter((e) => e.lastError === AUTH_PAUSE_ERROR)
    .modify((e) => {
      // Not deleted: a paused entry may already be on the server (see
      // RESUMED_AFTER_SIGN_IN), so it must not look never-submitted.
      e.lastError = RESUMED_AFTER_SIGN_IN;
    });
  wakeRefereeSync();
}

/**
 * Undo of a deleted shot (REF-21): queue it again with the same values at
 * its old ordinal. Only if a new shot took that place meanwhile does it get
 * the lowest free one. Null when the round has no room left.
 */
export async function requeueShot(shot: CachedShot, shotCount: number): Promise<CachedShot | null> {
  const restored = await queueShot({
    assignmentId: shot.roundAssignmentId,
    shotCount,
    preferredOrd: shot.ord,
    fields: () => ({
      valueNumeric: shot.valueNumeric,
      zoneCode: shot.zoneCode,
      firstShotTimeMs: shot.firstShotTimeMs,
      elapsedTimeMs: shot.elapsedTimeMs,
      ...(shot.source ? { source: shot.source } : {}),
    }),
  });
  wakeRefereeSync();
  return restored;
}

/**
 * Queue a referee penalty (points come from the server's scoring schema) and
 * wake the uploader. Show it via `mergePenaltyView` (server penalties from
 * liveAssignments + the queued outbox entries).
 */
export async function addPenalty(input: {
  roundAssignmentId: string;
  code: PenaltyCode;
  note?: string;
}): Promise<PenaltyAddPayload> {
  const queued = await queuePenalty({
    assignmentId: input.roundAssignmentId,
    code: input.code,
    ...(input.note ? { note: input.note } : {}),
  });
  wakeRefereeSync();
  return queued;
}

/** Remove a penalty by its clientIdempotencyKey (until the round is confirmed). */
export async function removePenalty(
  roundAssignmentId: string,
  clientIdempotencyKey: string,
): Promise<void> {
  await removeQueuedPenalty(roundAssignmentId, clientIdempotencyKey);
  wakeRefereeSync();
}

/**
 * Disqualify a round (manual DQ or PAR auto-DQ). Sent directly, or — like
 * `saveRoundTiming` — queued (`round.dq`) behind this round's unsent changes,
 * or when offline / the login expired. Queued, it sits in the FIFO outbox
 * ahead of any confirmation queued later, so a confirm can never land
 * without the DQ (same as the native app's `disqualifyRoundOrQueue`).
 * `queued` = not on the server yet; `authExpired` = it waits for the new
 * sign-in. Other server errors throw.
 */
export async function disqualifyRoundOrQueue(
  assignmentId: string,
  input: { reason: string; terminal: boolean },
): Promise<{ queued: boolean; authExpired: boolean }> {
  const payload = { roundAssignmentId: assignmentId, ...input };
  const unresolved = await getLocalDb()
    .outbox.where('status')
    .anyOf('pending', 'inflight')
    .filter((e) => assignmentOf(e) === assignmentId)
    .count();
  if (unresolved > 0) {
    await queueRoundOp('round.dq', payload);
    return { queued: true, authExpired: false };
  }
  try {
    await api.disqualifyRound(assignmentId, input.reason, { terminal: input.terminal });
    return { queued: false, authExpired: false };
  } catch (err) {
    const auth = err instanceof UnauthenticatedError;
    if (!auth && !isOffline(err)) throw err;
    await queueRoundOp('round.dq', payload);
    return { queued: true, authExpired: auth };
  }
}

export type TimingPatch = { firstShotTimeMs?: number; elapsedTimeMs?: number };
export type TimingSaveResult = { authExpired: boolean; sent: TimingPatch };

/**
 * Save round timing. Offline (REF-3): while anything of this round is still
 * queued (e.g. an earlier timing), a direct write would be overwritten when
 * the queue replays — so queue behind it. FIFO also keeps it ahead of a
 * confirm. Offline / expired login queue too (`authExpired` = the login ran
 * out; the value waits in the queue for the new sign-in). Other errors throw.
 */
async function saveRoundTiming(
  assignmentId: string,
  patch: TimingPatch,
): Promise<{ authExpired: boolean }> {
  const payload = { roundAssignmentId: assignmentId, ...patch };
  const unresolved = await getLocalDb()
    .outbox.where('status')
    .anyOf('pending', 'inflight')
    .filter((e) => assignmentOf(e) === assignmentId)
    .count();
  if (unresolved > 0) {
    await queueRoundOp('round.timing', payload);
    return { authExpired: false };
  }
  try {
    await api.setRoundTiming(payload);
    return { authExpired: false };
  } catch (err) {
    const auth = err instanceof UnauthenticatedError;
    if (!auth && !isOffline(err)) throw err;
    await queueRoundOp('round.timing', payload);
    return { authExpired: auth };
  }
}

type TimingWaiter = { resolve: (r: TimingSaveResult) => void; reject: (err: unknown) => void };
type TimingLane = { busy: boolean; next: { patch: TimingPatch; waiters: TimingWaiter[] } | null };
const timingLanes = new Map<string, TimingLane>();

/**
 * `saveRoundTiming`, serialized per assignment (same as the native app's
 * `saveRoundTimingInOrder`): a save starts only after the previous one of
 * the same round settled, so an older request can never reach the server
 * after a newer correction and overwrite it. Saves that pile up while one is
 * in flight are coalesced (field-wise, newest wins) and go out as ONE
 * request; every caller of that batch settles with it. `sent` = the values
 * actually submitted (for the screen's `latestTiming`).
 */
export function saveRoundTimingInOrder(
  assignmentId: string,
  patch: TimingPatch,
): Promise<TimingSaveResult> {
  let lane = timingLanes.get(assignmentId);
  if (!lane) {
    lane = { busy: false, next: null };
    timingLanes.set(assignmentId, lane);
  }
  const l = lane;
  return new Promise<TimingSaveResult>((resolve, reject) => {
    if (l.next) {
      l.next.patch = { ...l.next.patch, ...patch };
      l.next.waiters.push({ resolve, reject });
    } else {
      l.next = { patch: { ...patch }, waiters: [{ resolve, reject }] };
    }
    if (!l.busy) void runTimingLane(assignmentId, l);
  });
}

async function runTimingLane(assignmentId: string, lane: TimingLane): Promise<void> {
  lane.busy = true;
  try {
    while (lane.next) {
      const batch = lane.next;
      lane.next = null;
      try {
        const res = await saveRoundTiming(assignmentId, batch.patch);
        for (const w of batch.waiters) w.resolve({ ...res, sent: batch.patch });
      } catch (err) {
        for (const w of batch.waiters) w.reject(err);
      }
    }
  } finally {
    lane.busy = false;
    if (timingLanes.get(assignmentId) === lane) timingLanes.delete(assignmentId);
  }
}
